import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiLoader, 
  FiEye,
  FiSave,
  FiX,
  FiSearch,
  FiUser,
  FiMapPin,
  FiStar,
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight
} from "react-icons/fi";
import { FaMale, FaFemale } from "react-icons/fa";

const TestimonialsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  
  // Form states
  const [newTestimonial, setNewTestimonial] = useState({ 
    name: "", 
    location: "", 
    title: "", 
    feedback: "", 
    gender: "neutral" 
  });
  
  const [editingTestimonial, setEditingTestimonial] = useState({ 
    id: null, 
    name: "", 
    location: "", 
    title: "", 
    feedback: "", 
    gender: "neutral" 
  });
  
  // Loading states
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const API_URL = "https://api.dreamtoday.in/api/admin";

  // Fetch all testimonials
  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/alltestimonial`, {
        withCredentials: true
      });
      
      console.log("Testimonials response:", response.data);
      
      if (response.data.success) {
        setTestimonials(response.data.testimonials || []);
        setFilteredTestimonials(response.data.testimonials || []);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      toast.error("Failed to fetch testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Search and filter
  useEffect(() => {
    if (searchTerm) {
      const filtered = testimonials.filter(t => 
        t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.feedback?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t._id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTestimonials(filtered);
    } else {
      setFilteredTestimonials(testimonials);
    }
    setCurrentPage(1);
  }, [searchTerm, testimonials]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTestimonials.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTestimonials.length / itemsPerPage);

  // Handle create testimonial
  const handleCreateTestimonial = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      if (!newTestimonial.name || !newTestimonial.feedback) {
        toast.error("Name and feedback are required");
        setCreating(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/createtestimonial`,
        newTestimonial,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Testimonial created successfully!");
        setShowCreateModal(false);
        setNewTestimonial({ name: "", location: "", title: "", feedback: "", gender: "neutral" });
        fetchTestimonials();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create testimonial");
    } finally {
      setCreating(false);
    }
  };

  // Handle update testimonial
  const handleUpdateTestimonial = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      if (!editingTestimonial.name || !editingTestimonial.feedback) {
        toast.error("Name and feedback are required");
        setUpdating(false);
        return;
      }

      const response = await axios.put(
        `${API_URL}/updatetestimonial/${editingTestimonial.id}`,
        {
          name: editingTestimonial.name,
          location: editingTestimonial.location,
          title: editingTestimonial.title,
          feedback: editingTestimonial.feedback,
          gender: editingTestimonial.gender
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Testimonial updated successfully!");
        setShowEditModal(false);
        setEditingTestimonial({ id: null, name: "", location: "", title: "", feedback: "", gender: "neutral" });
        fetchTestimonials();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update testimonial");
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete testimonial
  const handleDeleteTestimonial = async () => {
    if (!selectedTestimonial) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/deletetestimonial/${selectedTestimonial._id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Testimonial deleted successfully!");
        setShowDeleteModal(false);
        setSelectedTestimonial(null);
        fetchTestimonials();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete testimonial");
    } finally {
      setDeleting(false);
    }
  };

  // Get gender icon
  const getGenderIcon = (gender) => {
    switch(gender) {
      case 'male': return <FaMale className="text-blue-600" />;
      case 'female': return <FaFemale className="text-pink-600" />;
      default: return <FiUser className="text-gray-600" />;
    }
  };

  // Format date
  const formatDate = (date) => new Date(date).toLocaleDateString();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FiLoader className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FiStar className="text-yellow-500" />
              Testimonials Management
            </h1>
            <p className="text-gray-600 mt-1">Total {filteredTestimonials.length} testimonials found</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiPlus /> Add Testimonial
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, location, title, feedback or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Table */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiStar className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Testimonials Found</h3>
            <p className="text-gray-500 mb-6">Get started by adding your first testimonial</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Testimonial
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Location</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Title</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Feedback</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {currentItems.map((testimonial, idx) => (
                      <tr key={testimonial._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {indexOfFirstItem + idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              {getGenderIcon(testimonial.gender)}
                            </div>
                            <div>
                              <div className="font-medium">{testimonial.name}</div>
                              <div className="text-xs text-gray-500">ID: {testimonial._id.slice(-6)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <FiMapPin className="text-red-500" size={14} />
                            <span className="text-sm">{testimonial.location || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <FiUser className="text-blue-500" size={14} />
                            <span className="text-sm">{testimonial.title || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-600 max-w-xs truncate">
                            "{testimonial.feedback}"
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(testimonial.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedTestimonial(testimonial);
                                setShowViewModal(true);
                              }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              onClick={() => {
                                setEditingTestimonial({
                                  id: testimonial._id,
                                  name: testimonial.name,
                                  location: testimonial.location || "",
                                  title: testimonial.title || "",
                                  feedback: testimonial.feedback,
                                  gender: testimonial.gender || "neutral"
                                });
                                setShowEditModal(true);
                              }}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedTestimonial(testimonial);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTestimonials.length)} of {filteredTestimonials.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiChevronLeft />
                  </button>
                  <span className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Add Testimonial</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleCreateTestimonial} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name *</label>
                    <input
                      type="text"
                      value={newTestimonial.name}
                      onChange={(e) => setNewTestimonial({...newTestimonial, name: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Location</label>
                    <input
                      type="text"
                      value={newTestimonial.location}
                      onChange={(e) => setNewTestimonial({...newTestimonial, location: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="New York, USA"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Title/Role</label>
                    <input
                      type="text"
                      value={newTestimonial.title}
                      onChange={(e) => setNewTestimonial({...newTestimonial, title: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="CEO at Company"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Gender</label>
                    <select
                      value={newTestimonial.gender}
                      onChange={(e) => setNewTestimonial({...newTestimonial, gender: e.target.value})}
                      className="w-full p-2 border rounded"
                    >
                      <option value="neutral">Neutral</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Feedback *</label>
                    <textarea
                      value={newTestimonial.feedback}
                      onChange={(e) => setNewTestimonial({...newTestimonial, feedback: e.target.value})}
                      rows="4"
                      className="w-full p-2 border rounded"
                      placeholder="Great service! Highly recommended..."
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {creating ? "Creating..." : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Edit Testimonial</h2>
                  <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleUpdateTestimonial} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name *</label>
                    <input
                      type="text"
                      value={editingTestimonial.name}
                      onChange={(e) => setEditingTestimonial({...editingTestimonial, name: e.target.value})}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Location</label>
                    <input
                      type="text"
                      value={editingTestimonial.location}
                      onChange={(e) => setEditingTestimonial({...editingTestimonial, location: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Title/Role</label>
                    <input
                      type="text"
                      value={editingTestimonial.title}
                      onChange={(e) => setEditingTestimonial({...editingTestimonial, title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Gender</label>
                    <select
                      value={editingTestimonial.gender}
                      onChange={(e) => setEditingTestimonial({...editingTestimonial, gender: e.target.value})}
                      className="w-full p-2 border rounded"
                    >
                      <option value="neutral">Neutral</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Feedback *</label>
                    <textarea
                      value={editingTestimonial.feedback}
                      onChange={(e) => setEditingTestimonial({...editingTestimonial, feedback: e.target.value})}
                      rows="4"
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {updating ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedTestimonial && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Testimonial Details</h2>
                  <button onClick={() => setShowViewModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Header with avatar */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-3xl">
                      {getGenderIcon(selectedTestimonial.gender)}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{selectedTestimonial.name}</h3>
                      {selectedTestimonial.title && (
                        <p className="text-gray-600">{selectedTestimonial.title}</p>
                      )}
                      {selectedTestimonial.location && (
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <FiMapPin /> {selectedTestimonial.location}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Feedback */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-700 italic">"{selectedTestimonial.feedback}"</p>
                  </div>

                  {/* Meta info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-500">Testimonial ID</p>
                      <p className="text-sm font-mono">{selectedTestimonial._id}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm">{formatDate(selectedTestimonial.createdAt)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setEditingTestimonial({
                        id: selectedTestimonial._id,
                        name: selectedTestimonial.name,
                        location: selectedTestimonial.location || "",
                        title: selectedTestimonial.title || "",
                        feedback: selectedTestimonial.feedback,
                        gender: selectedTestimonial.gender || "neutral"
                      });
                      setShowEditModal(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedTestimonial && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center">
                <FiTrash2 className="text-5xl text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Delete Testimonial</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete testimonial from <span className="font-semibold">{selectedTestimonial.name}</span>?
                </p>
                
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteTestimonial}
                    disabled={deleting}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {deleting ? <FiLoader className="animate-spin" /> : <FiTrash2 />}
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestimonialsManagement;